import Backend from 'App/Dataman-Backend';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, ApiError } from '@/app/api/core/http';
import { createImage, updateImage, uploadImages } from '@/app/api/core/images';
import { CAT_PRODUCTS } from 'App/utils/Collections';
import { searchProducts } from 'App/services/products/productSearch';
import { PRODUCTS_PATHS } from './paths';

async function cloneProduct(id, params) {
  return apiPost(`${PRODUCTS_PATHS.products}/${id}/clone`, { params }).then((response) => {
    if (response.status === 201) {
      return response.data;
    } else {
      throw new ApiError('Could not clone product', response);
    }
  });
}

async function cloneProducts(params) {
  return apiPost(`${PRODUCTS_PATHS.products}/clone`, { params }).then((response) => {
    const status = (response.status / 100) >> 0;
    if (status === 2) {
      return response.data;
    } else {
      throw new ApiError('Could not clone product', response.data.title);
    }
  });
}

export const productsApiAdapter = {
  getProducts: searchProducts,
  getProductDetails: (id, headers) => {
    if (headers) {
      return apiGet(`${PRODUCTS_PATHS.products}/${id}`, {
        headers: headers,
        is_protected: true,
      });
    } else {
      return apiGet(`${PRODUCTS_PATHS.products}/${id}`, { is_protected: true });
    }
  },
  getProductsStats: (params) => {
    return apiGet(PRODUCTS_PATHS.stats, {
      params,
      headers: { 'WIMS-loggable': true },
      is_protected: true,
    });
  },
  getProductHistory: (id) => apiGet(`${PRODUCTS_PATHS.products}/${id}/history`),
  getProductSnapshotById: (id, snapshot_id) =>
    apiGet(`${PRODUCTS_PATHS.products}/${id}/history/snapshots/${snapshot_id}`),
  getProductImages: (product_id, params = {}) =>
    apiGet(PRODUCTS_PATHS.images, {
      params: {
        'product.id': product_id,
        itemsPerPage: 200,
        ...params,
      },
    }),
  getProductImage: (id) => apiGet(`${PRODUCTS_PATHS.images}/${id}`),
  getProductFile: async (fileId) => {
    const res = await Backend.loadBlobData({
      url: `${PRODUCTS_PATHS.files}/${fileId}`,
    });

    return res;
  },
  cloneProduct,
  cloneProducts,
  createProduct: (data) => {
    return apiPost(PRODUCTS_PATHS.products, { data, is_protected: true }).then(async (res) => {
      if (res.status === 201) {
        await uploadImages(CAT_PRODUCTS, data.images, {
          product_id: res.data.id,
        }).catch();
      } else {
        throw new ApiError('Can not save product', res);
      }
      return res;
    });
  },
  updateProduct: async (id, data) => {
    const { images, ...rest } = data || {};
    const res = await Backend.patch({
      data: rest,
      headers: { 'content-type': 'application/merge-patch+json' },
      url: `${PRODUCTS_PATHS.products}/${id}`,
      is_protected: true,
    });
    if (images !== void 0) {
      await uploadImages(CAT_PRODUCTS, images, { product_id: id }).catch();
    }
    return res;
  },
  products: {
    remove: (id) => apiDelete(`${PRODUCTS_PATHS.products}/${id}`),
    toggleRedesign: (id, isRedesigned) => {
      return apiPut(`${PRODUCTS_PATHS.products}/${id}/images/redesign`, {
        data: { isRedesigned },
      });
    },
  },
  productImages: {
    create: (img, product_id, is_default = false, session_interval = 0) =>
      createImage(CAT_PRODUCTS, img, {
        product_id,
        is_default,
        session_interval,
      }),
    createWithLink: (link, product_id, is_default = false) =>
      apiPost(PRODUCTS_PATHS.images, { params: { product_id, is_default }, data: { link } }),
    update: (id, data) => updateImage(CAT_PRODUCTS, id, data),
    remove: (id) => apiDelete(`${PRODUCTS_PATHS.images}/${id}`),
  },
  productLines: {
    getList: (params) => apiGet(PRODUCTS_PATHS.productLines + PRODUCTS_PATHS.suffix, { params }),
    getItem: (id) => apiGet(`${PRODUCTS_PATHS.productLines}/${id}`),
  },
  recommendedProducts: {
    getList: (params) => apiGet(PRODUCTS_PATHS.recommended + PRODUCTS_PATHS.suffix, { params }),
    getItem: (id, params) => apiGet(`${PRODUCTS_PATHS.recommended}/${id}`, { params }),
    update: (id, data) => {
      let preparedData = {};
      if (data.products && data.products.length) {
        preparedData.comment = data.products[0].comment;
      }
      if (data.read !== undefined) {
        preparedData.read = data.read;
      }
      if (data.sessionInterval) {
        preparedData.sessionInterval = data.sessionInterval;
      }
      return apiPatch(`${PRODUCTS_PATHS.recommended}/${id}`, {
        data: preparedData,
        headers: { 'content-type': 'application/merge-patch+json' },
      });
    },
    create: (data) => apiPost(PRODUCTS_PATHS.recommended, { data }),
  },
  // Image search is an external service: a failed response is returned as-is instead of throwing
  searchProductsByImage: (data) => {
    try {
      return Backend.request(PRODUCTS_PATHS.imageSearch, 'post', {
        data,
        auth: true,
      });
    } catch (err) {
      const { status } = err?.response || {};
      if (status >= 400) {
        return err?.response;
      } else {
        const { message } = err.toJSON();
        throw new Error(message);
      }
    }
  },
};

export const productsFilterAdapter = {
  getProductStatuses: (params) => apiGet(PRODUCTS_PATHS.statuses, params),
};
